import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, BarChart3, CalendarDays, CalendarClock, Inbox, LayoutGrid, Plus, Receipt, TrendingUp, UserPlus, Wallet, Briefcase, Clock,
} from 'lucide-react';
import { format, addDays, isToday, isTomorrow, parseISO } from 'date-fns';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { ChartTooltip } from '@/components/ui/chart';
import { Button } from '@/components/ui/button';
import { GettingStartedChecklist } from '@/components/admin/GettingStartedChecklist';
import { CleanerNotificationBar } from '@/components/admin/CleanerNotificationBar';
import { UnassignedJobsAlert } from '@/components/admin/UnassignedJobsAlert';
import {
  EmptyState, Panel, PersonAvatar, StatCard, StatusChip, adminButton, adminLink, useAdminPageTitle,
} from '@/components/admin/ui';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { formatMoney, parseMoneyValue } from '@/lib/currency';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useDashboardData, type DashboardJob } from '@/hooks/useDashboardData';
import { useMyProfileName } from '@/hooks/useMyProfileName';
import { useCompanyInfoBrief } from '@/hooks/useCompanyInfo';
import { cn } from '@/lib/utils';
import { InsightsView } from '@/components/admin/insights/InsightsView';
import { JobPaymentChip } from '@/components/admin/jobs/JobPaymentChip';
import { isInsightRange, type InsightRange } from '@/lib/dashboard-insights';

const VIOLET = 'hsl(var(--adm-violet))';
const MINT = 'hsl(172 46% 48%)';
const SUN = 'hsl(42 95% 52%)';
const BLUE = 'hsl(214 84% 58%)';

const subDays = (date: Date, days: number) => addDays(date, -days);
const ymd = (date: Date) => format(date, 'yyyy-MM-dd');
const pctChange = (current: number, previous: number): number | null =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

const greetingFor = (date: Date) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard = () => {
  useAdminPageTitle('Dashboard');
  const { orgId, loading: orgLoading, noOrgSelected } = useAdminOrg();
  const { user, staffRole } = useAuth();
  const isCleaner = staffRole === 'cleaner';
  const [searchParams, setSearchParams] = useSearchParams();
  // Business reporting is for owners/managers only.
  const view: 'overview' | 'insights' = !isCleaner && searchParams.get('view') === 'insights' ? 'insights' : 'overview';
  const rangeParam = searchParams.get('range');
  const insightRange: InsightRange = isInsightRange(rangeParam) ? rangeParam : '30d';
  const setView = (next: 'overview' | 'insights') =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'insights') params.set('view', 'insights');
        else {
          params.delete('view');
          params.delete('range');
        }
        return params;
      },
      { replace: true },
    );
  const setInsightRange = (next: InsightRange) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('view', 'insights');
        params.set('range', next);
        return params;
      },
      { replace: true },
    );
  const { data: orgSettings } = useOrgSettings(orgId);
  const currency = orgSettings?.currency;
  const { data: profileName } = useMyProfileName(user?.id);
  const { data: companyInfo } = useCompanyInfoBrief(orgId);

  const { data: dashboardData, isLoading: dataLoading } = useDashboardData(orgId, orgLoading);

  const upcomingJobs = useMemo(() => dashboardData?.upcomingJobs ?? [], [dashboardData]);
  const futureJobs = useMemo(() => dashboardData?.futureJobs ?? [], [dashboardData]);
  const allJobs = useMemo(() => dashboardData?.allJobs ?? [], [dashboardData]);
  const lastMonthJobs = dashboardData?.lastMonthJobs ?? [];
  const last30DaysJobs = useMemo(() => dashboardData?.last30DaysJobs ?? [], [dashboardData]);
  const services = useMemo(() => dashboardData?.services ?? [], [dashboardData]);
  const teamMembers = dashboardData?.teamMembers ?? [];
  const enquiries = useMemo(() => dashboardData?.enquiries ?? [], [dashboardData]);
  const lastMonthEnquiries = useMemo(() => dashboardData?.lastMonthEnquiries ?? [], [dashboardData]);

  const loading = orgLoading || dataLoading;
  const fmt = (value: number) => formatMoney(value, currency, { maximumFractionDigits: 0 });

  const priceOf = useMemo(() => {
    const map = new Map(services.map((s) => [s.id, parseMoneyValue(s.price) ?? 0]));
    return (job: DashboardJob) => (job.service_id ? map.get(job.service_id) ?? 0 : 0);
  }, [services]);
  const calculateRevenue = (jobs: DashboardJob[]) => jobs.reduce((total, job) => total + priceOf(job), 0);
  // A card booking still waiting for payment isn't a booking yet.
  const live = (jobs: DashboardJob[]) => jobs.filter((j) => j.status !== 'cancelled' && j.status !== 'awaiting_payment');

  // Headline metrics
  const todaysJobs = live(upcomingJobs.filter((j) => isToday(parseISO(j.scheduled_date))));
  const tomorrowsJobs = live([...upcomingJobs, ...futureJobs].filter((j) => isTomorrow(parseISO(j.scheduled_date))));
  const scheduledJobs = allJobs.filter((j) => j.status === 'scheduled');
  const completedJobs = allJobs.filter((j) => j.status === 'completed');
  const cancelledJobs = allJobs.filter((j) => j.status === 'cancelled');
  const inProgressJobs = allJobs.filter((j) => j.status === 'in_progress');
  const otherJobs = allJobs.length - scheduledJobs.length - completedJobs.length - cancelledJobs.length - inProgressJobs.length;

  const projectedRevenue = calculateRevenue(scheduledJobs.concat(completedJobs));
  const completedRevenue = calculateRevenue(completedJobs);
  const lastMonthRevenue = calculateRevenue(lastMonthJobs);
  const revenueChange = pctChange(projectedRevenue, lastMonthRevenue);
  const jobsChange = pctChange(allJobs.length, lastMonthJobs.length);
  const enquiriesChange = pctChange(enquiries.length, lastMonthEnquiries.length);

  const pendingEnquiries = enquiries.filter((e) => e.status === 'pending').length;
  const contactedEnquiries = enquiries.filter((e) => e.status === 'contacted').length;
  const convertedEnquiries = enquiries.filter((e) => e.status === 'converted').length;

  // 14-day daily series (oldest → today) for sparklines + the revenue chart.
  const daily = useMemo(() => {
    const today = new Date();
    const enquiryDays = [...lastMonthEnquiries, ...enquiries].map((e) => e.created_at.slice(0, 10));
    return Array.from({ length: 14 }, (_, i) => {
      const date = subDays(today, 13 - i);
      const key = ymd(date);
      const dayJobs = last30DaysJobs.filter((j) => j.scheduled_date === key && j.status !== 'cancelled');
      return {
        key,
        label: format(date, 'MMM d'),
        short: format(date, 'EEE').slice(0, 2),
        jobs: dayJobs.length,
        revenue: dayJobs.reduce((t, j) => t + priceOf(j), 0),
        enquiries: enquiryDays.filter((d) => d === key).length,
      };
    });
  }, [last30DaysJobs, enquiries, lastMonthEnquiries, priceOf]);

  const previousFortnightRevenue = useMemo(() => {
    const from = ymd(subDays(new Date(), 27));
    const to = ymd(subDays(new Date(), 14));
    return last30DaysJobs
      .filter((j) => j.status !== 'cancelled' && j.scheduled_date >= from && j.scheduled_date <= to)
      .reduce((t, j) => t + priceOf(j), 0);
  }, [last30DaysJobs, priceOf]);
  const fortnightRevenue = daily.reduce((t, d) => t + d.revenue, 0);

  // Next 7 days (today → +6) from this-week + future jobs.
  const nextSevenDays = useMemo(() => {
    const pool = [...upcomingJobs, ...futureJobs].filter((j) => j.status !== 'cancelled');
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(new Date(), i);
      const key = ymd(date);
      const dayJobs = pool.filter((j) => j.scheduled_date === key);
      return {
        day: i === 0 ? 'Today' : format(date, 'EEE'),
        fullDate: format(date, 'EEE, MMM d'),
        jobs: dayJobs.length,
        revenue: dayJobs.reduce((t, j) => t + priceOf(j), 0),
      };
    });
  }, [upcomingJobs, futureJobs, priceOf]);

  const bookingRequests = useMemo(
    () =>
      [...upcomingJobs, ...futureJobs]
        .filter((j) => j.status === 'pending_confirmation')
        .sort((a, b) => `${a.scheduled_date} ${a.start_time}`.localeCompare(`${b.scheduled_date} ${b.start_time}`)),
    [upcomingJobs, futureJobs],
  );

  const teamPerformance = teamMembers
    .map((member) => {
      const memberJobs = allJobs.filter((j) => j.team_member_id === member.id);
      const completed = memberJobs.filter((j) => j.status === 'completed').length;
      const scheduled = memberJobs.filter((j) => j.status === 'scheduled').length;
      return { id: member.id, name: member.name, completed, scheduled, total: completed + scheduled, revenue: calculateRevenue(memberJobs) };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);
  const maxTeamTotal = Math.max(1, ...teamPerformance.map((m) => m.total));

  const servicePopularity = services
    .map((service) => {
      const serviceJobs = allJobs.filter((j) => j.service_id === service.id);
      return { id: service.id, name: service.name, jobs: serviceJobs.length, revenue: calculateRevenue(serviceJobs) };
    })
    .filter((s) => s.jobs > 0)
    .sort((a, b) => b.jobs - a.jobs)
    .slice(0, 5);
  const maxServiceJobs = Math.max(1, ...servicePopularity.map((s) => s.jobs));

  const getTeamMemberName = (id: string | null) => {
    if (!id) return 'Unassigned';
    return teamMembers.find((t) => t.id === id)?.name || 'Unknown';
  };
  const getServiceName = (id: string | null) => {
    if (!id) return 'General';
    return services.find((s) => s.id === id)?.name || 'Service';
  };
  const formatJobDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  const renderJobItem = (job: DashboardJob) => {
    const cleaner = getTeamMemberName(job.team_member_id);
    const date = parseISO(job.scheduled_date);
    const today = isToday(date);
    return (
      <div
        key={job.id}
        data-testid={`dashboard-upcoming-job-${job.id}`}
        className="flex items-center gap-3 py-2.5"
      >
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl border text-center leading-none',
            today ? 'border-primary/20 bg-secondary text-primary' : 'border-border bg-muted/50 text-foreground',
          )}
        >
          <span className={cn('text-[10px] font-semibold uppercase', today ? 'text-primary/80' : 'text-muted-foreground')}>
            {format(date, 'EEE')}
          </span>
          <span className="adm-num mt-0.5 text-[15px] font-semibold">{format(date, 'd')}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-semibold text-foreground">{job.title || getServiceName(job.service_id)}</p>
          <p className="truncate text-[12px] text-muted-foreground">
            {job.client_name ? job.client_name : 'No client'}
            <span className="sm:hidden"> · {cleaner}</span>
          </p>
        </div>
        <div className="hidden w-[150px] shrink-0 items-center gap-2 sm:flex">
          {job.team_member_id ? (
            <PersonAvatar name={cleaner} seed={job.team_member_id} size={24} />
          ) : (
            <PersonAvatar name={null} size={24} placeholder />
          )}
          <span className={cn('truncate text-[12.5px]', job.team_member_id ? 'text-foreground' : 'text-muted-foreground')}>
            {cleaner}
          </span>
        </div>
        <div className="w-[76px] shrink-0 text-right">
          <p className="adm-num text-[13px] font-semibold text-foreground">{job.start_time.slice(0, 5)}</p>
          <p className="text-[11.5px] text-muted-foreground">{formatJobDate(job.scheduled_date)}</p>
        </div>
        <StatusChip status={job.status} className="hidden md:inline-flex" />
      </div>
    );
  };

  if (noOrgSelected) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-3 text-center p-8">
        <p className="text-lg font-medium">No organisation selected</p>
        <p className="text-sm text-muted-foreground">Use the org switcher to select a tenant and view their dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 rounded-lg bg-muted w-72"></div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-[118px] rounded-2xl bg-card border border-border"></div>
            ))}
          </div>
          <div className="h-80 rounded-2xl bg-card border border-border"></div>
        </div>
      </div>
    );
  }

  const firstName = (profileName || (user?.user_metadata?.name as string | undefined) || '').trim().split(/\s+/)[0];
  const companyName = companyInfo?.company_name;
  const now = new Date();

  const quickActions = [
    { to: '/admin/schedule?new=job', label: 'Job', icon: CalendarDays },
    { to: '/admin/invoices', label: 'Invoice', icon: Receipt },
    { to: '/admin/team', label: 'Team member', icon: UserPlus },
    { to: '/admin/enquiries', label: 'Enquiries', icon: Inbox, plain: true },
  ];

  const statusSegments = [
    { key: 'scheduled', label: 'Scheduled', value: scheduledJobs.length, color: BLUE },
    { key: 'in_progress', label: 'In progress', value: inProgressJobs.length, color: SUN },
    { key: 'completed', label: 'Completed', value: completedJobs.length, color: MINT },
    { key: 'other', label: 'Pending / other', value: Math.max(0, otherJobs), color: VIOLET },
    { key: 'cancelled', label: 'Cancelled', value: cancelledJobs.length, color: 'hsl(352 75% 62%)' },
  ];
  const statusTotal = statusSegments.reduce((t, s) => t + s.value, 0);

  return (
    <div className="space-y-5 p-4 md:p-6 lg:p-8">
      {/* Greeting */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="text-[24px] font-semibold leading-tight tracking-tight text-foreground md:text-[26px]">
            {greetingFor(now)}{firstName ? `, ${firstName}` : ''} <span aria-hidden="true">👋</span>
          </p>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            {format(now, 'EEEE, d MMMM')} · here's what's happening{companyName ? ` at ${companyName}` : ''} today.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Cleaner-only: newly assigned jobs + schedule changes (no-op for owners) */}
          <CleanerNotificationBar orgId={orgId} jobs={[...upcomingJobs, ...futureJobs]} />
          {/* Owner/admin-only: upcoming jobs still needing a cleaner (no-op for cleaners) */}
          <UnassignedJobsAlert jobs={[...upcomingJobs, ...futureJobs]} />
          <Button asChild size="sm" variant="outline" className={adminButton.outline}>
            <Link to="/admin/schedule">
              <CalendarClock />
              Open calendar
            </Link>
          </Button>
        </div>
      </div>

      {/* Overview / Insights tabs (Fikri "Recruitment / HR Manager dashboard" switch) */}
      {!isCleaner && (
        <div role="tablist" aria-label="Dashboard view" className="flex items-center gap-5 border-b border-border">
          {([
            { key: 'overview', label: 'Overview', icon: LayoutGrid },
            { key: 'insights', label: 'Insights', icon: BarChart3 },
          ] as const).map((tab) => {
            const Icon = tab.icon;
            const active = view === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`dashboard-tab-${tab.key}`}
                onClick={() => setView(tab.key)}
                className={cn(
                  '-mb-px inline-flex items-center gap-1.5 border-b-2 pb-2.5 pt-1 text-[13.5px] font-medium transition-colors',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {view === 'insights' ? (
        <InsightsView orgId={orgId} currency={currency} range={insightRange} onRangeChange={setInsightRange} />
      ) : (
      <>
      {/* Quick create (Fikri-style chips) */}
      {!isCleaner && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Quick actions">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.to}
                className="group inline-flex h-9 items-center gap-2 rounded-full border border-border bg-card pl-1.5 pr-3 text-[13px] font-medium text-foreground shadow-[var(--adm-shadow-xs)] transition-colors hover:border-primary/40"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {action.label}
                {!action.plain && <Plus className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />}
              </Link>
            );
          })}
        </div>
      )}

      {/* Getting started checklist (auto-hides when complete or dismissed) */}
      <GettingStartedChecklist />

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Today's jobs"
          icon={<Briefcase />}
          value={todaysJobs.length}
          hint={`${tomorrowsJobs.length} tomorrow`}
          trend={daily.map((d) => d.jobs)}
          trendType="bar"
          color={VIOLET}
        />
        <StatCard
          label="Jobs this month"
          icon={<CalendarDays />}
          value={allJobs.length}
          delta={jobsChange}
          deltaLabel="vs last month"
          hint={jobsChange === null ? `${completedJobs.length} completed` : undefined}
          trend={daily.map((d) => d.jobs)}
          color={BLUE}
        />
        <StatCard
          label="Revenue this month"
          icon={<Wallet />}
          value={fmt(projectedRevenue)}
          delta={revenueChange}
          deltaLabel="vs last month"
          hint={revenueChange === null ? `${fmt(completedRevenue)} completed` : undefined}
          trend={daily.map((d) => d.revenue)}
          color={MINT}
        />
        <StatCard
          label="Enquiries this month"
          icon={<Inbox />}
          value={enquiries.length}
          delta={enquiriesChange}
          deltaLabel="vs last month"
          hint={enquiriesChange === null ? `${pendingEnquiries} pending` : undefined}
          trend={daily.map((d) => d.enquiries)}
          trendType="bar"
          color={SUN}
        />
      </div>

      {/* Jobs + requests */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          data-testid="dashboard-upcoming-jobs"
          title="Upcoming Jobs"
          icon={<Clock className="h-4 w-4 text-primary" />}
          description={`${live(upcomingJobs).length} this week · ${live(futureJobs).length} later`}
          actions={
            <Link to="/admin/schedule" aria-label="View all jobs on the schedule" className={adminLink}>
              View All
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          {upcomingJobs.length === 0 && futureJobs.length === 0 ? (
            <EmptyState
              icon={<CalendarDays />}
              title="No upcoming jobs this week"
              description="Schedule a job to see it here."
              action={
                <Link to="/admin/schedule" className={adminLink}>
                  Open schedule
                </Link>
              }
            />
          ) : (
            <div className="space-y-4">
              {upcomingJobs.length > 0 && (
                <section data-testid="dashboard-upcoming-this-week" aria-label="This week's jobs">
                  <h3 className="adm-label">This week</h3>
                  <div className="adm-divide">{upcomingJobs.slice(0, 6).map(renderJobItem)}</div>
                </section>
              )}
              {futureJobs.length > 0 && (
                <section data-testid="dashboard-upcoming-future" aria-label="Upcoming jobs after this week">
                  <h3 className="adm-label">Upcoming</h3>
                  <div className="adm-divide">{futureJobs.slice(0, 6).map(renderJobItem)}</div>
                </section>
              )}
            </div>
          )}
        </Panel>

        <div className="flex flex-col gap-4">
          {!isCleaner && (
            <Panel
              title="Booking requests"
              icon={
                bookingRequests.length > 0 ? (
                  <span className="adm-chip adm-chip--violet">{bookingRequests.length}</span>
                ) : undefined
              }
              description="Waiting for your approval"
              actions={
                <Link to="/admin/schedule" className={adminLink}>
                  Review
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              }
            >
              {bookingRequests.length === 0 ? (
                <p className="py-2 text-[13px] text-muted-foreground">No requests waiting — you're all caught up.</p>
              ) : (
                <ul className="adm-divide">
                  {bookingRequests.slice(0, 4).map((job) => (
                    <li key={job.id} className="flex items-center gap-3 py-2.5">
                      <PersonAvatar name={job.client_name || job.title} size={30} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-foreground">{job.client_name || job.title}</p>
                        <p className="truncate text-[12px] text-muted-foreground">
                          {getServiceName(job.service_id)} · {formatJobDate(job.scheduled_date)}, {job.start_time.slice(0, 5)}
                        </p>
                      </div>
                      <JobPaymentChip job={job} className="hidden sm:inline-flex" />
                      <StatusChip status="pending_confirmation">Request</StatusChip>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}

          <Panel
            title="Enquiries"
            description="This month"
            actions={
              <Link to="/admin/enquiries" className={adminLink}>
                View Enquiries
              </Link>
            }
          >
            <div className="space-y-3">
              {[
                { label: 'Pending', value: pendingEnquiries, color: SUN },
                { label: 'Contacted', value: contactedEnquiries, color: BLUE },
                { label: 'Converted', value: convertedEnquiries, color: MINT },
              ].map((row) => (
                <div key={row.label}>
                  <div className="mb-1 flex items-center justify-between text-[12.5px]">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="adm-num font-semibold text-foreground">{row.value}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${enquiries.length ? Math.max(4, (row.value / enquiries.length) * 100) : 0}%`,
                        background: row.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {/* Revenue + team */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Revenue"
          icon={<TrendingUp className="h-4 w-4 text-primary" />}
          description="Last 14 days, from booked services"
          actions={
            !isCleaner ? (
              <button type="button" onClick={() => setView('insights')} className={adminLink}>
                Full report
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : undefined
          }
        >
          <div className="mb-3 flex flex-wrap items-end gap-x-8 gap-y-2 border-b border-border pb-3">
            <div>
              <p className="text-[11.5px] text-muted-foreground">Last 14 days</p>
              <p className="adm-num text-[20px] font-semibold text-foreground">{fmt(fortnightRevenue)}</p>
            </div>
            <div>
              <p className="text-[11.5px] text-muted-foreground">Previous 14 days</p>
              <p className="adm-num text-[20px] font-semibold text-muted-foreground">{fmt(previousFortnightRevenue)}</p>
            </div>
            {pctChange(fortnightRevenue, previousFortnightRevenue) !== null && (
              <span
                className={cn(
                  'adm-delta mb-1',
                  fortnightRevenue >= previousFortnightRevenue ? 'adm-delta--up' : 'adm-delta--down',
                )}
              >
                {fortnightRevenue >= previousFortnightRevenue ? '+' : ''}
                {Math.round(pctChange(fortnightRevenue, previousFortnightRevenue)!)}%
              </span>
            )}
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="dashRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={VIOLET} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={VIOLET} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} interval={1} />
                <YAxis axisLine={false} tickLine={false} width={56} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => fmt(Number(v))} />
                <ChartTooltip
                  cursor={{ stroke: 'hsl(var(--border))' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload as (typeof daily)[number];
                      return (
                        <div className="rounded-lg border border-border bg-popover px-3 py-2 text-[12px] shadow-[var(--adm-shadow-md)]">
                          <p className="font-semibold">{d.label}</p>
                          <p className="text-primary">{fmt(d.revenue)}</p>
                          <p className="text-muted-foreground">{d.jobs} jobs</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area type="monotone" dataKey="revenue" stroke={VIOLET} strokeWidth={2} fill="url(#dashRevenueGradient)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          title="Team"
          description={`${teamMembers.length} active ${teamMembers.length === 1 ? 'member' : 'members'} · jobs this month`}
          actions={
            <Link to="/admin/team" className={adminLink}>
              Manage Team
            </Link>
          }
        >
          {teamPerformance.length === 0 ? (
            <EmptyState compact icon={<UserPlus />} title="No team data yet" description="Add cleaners to see their workload." />
          ) : (
            <ul className="space-y-3.5">
              {teamPerformance.map((member) => (
                <li key={member.id} className="flex items-center gap-3">
                  <PersonAvatar name={member.name} seed={member.id} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-[13px] font-semibold text-foreground">{member.name}</p>
                      <p className="adm-num shrink-0 text-[12px] text-muted-foreground">
                        <span className="font-semibold text-foreground">{member.total}</span> jobs
                      </p>
                    </div>
                    <div className="mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-muted">
                      <div style={{ width: `${(member.completed / maxTeamTotal) * 100}%`, background: MINT }} />
                      <div style={{ width: `${(member.scheduled / maxTeamTotal) * 100}%`, background: VIOLET }} />
                    </div>
                  </div>
                </li>
              ))}
              <li className="flex items-center gap-4 pt-1 text-[11.5px] text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: MINT }} />Completed</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: VIOLET }} />Scheduled</span>
              </li>
            </ul>
          )}
        </Panel>
      </div>

      {/* Week ahead, status mix, services */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Panel title="Next 7 days" description="Jobs and projected revenue by day">
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={nextSevenDays} margin={{ top: 6, right: 0, bottom: 0, left: -24 }} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <ChartTooltip
                  cursor={{ fill: 'hsl(var(--muted))' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload as (typeof nextSevenDays)[number];
                      return (
                        <div className="rounded-lg border border-border bg-popover px-3 py-2 text-[12px] shadow-[var(--adm-shadow-md)]">
                          <p className="font-semibold">{d.fullDate}</p>
                          <p className="text-primary">{d.jobs} jobs</p>
                          <p className="text-muted-foreground">{fmt(d.revenue)}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="jobs" fill={VIOLET} radius={[5, 5, 2, 2]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Job status" description="This month's breakdown">
          <div className="flex items-baseline gap-2">
            <p className="adm-num text-[26px] font-semibold text-foreground">{allJobs.length}</p>
            <p className="text-[12.5px] text-muted-foreground">jobs this month</p>
          </div>
          <div className="mt-3 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-muted">
            {statusTotal > 0 &&
              statusSegments
                .filter((s) => s.value > 0)
                .map((s) => <div key={s.key} style={{ width: `${(s.value / statusTotal) * 100}%`, background: s.color }} />)}
          </div>
          <ul className="mt-4 space-y-2">
            {statusSegments
              .filter((s) => s.key !== 'other' || s.value > 0)
              .map((s) => (
                <li key={s.key} className="flex items-center justify-between text-[12.5px]">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="adm-num font-semibold text-foreground">{s.value}</span>
                </li>
              ))}
          </ul>
        </Panel>

        <Panel title="Popular services" description="Most booked this month" className="md:col-span-2 xl:col-span-1">
          {servicePopularity.length === 0 ? (
            <EmptyState compact icon={<Receipt />} title="No service data yet" description="Bookings will show up here." />
          ) : (
            <ul className="space-y-3">
              {servicePopularity.map((service, index) => (
                <li key={service.id}>
                  <div className="mb-1 flex items-center justify-between gap-3 text-[12.5px]">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="adm-num w-4 text-muted-foreground">{index + 1}</span>
                      <span className="truncate font-medium text-foreground">{service.name}</span>
                    </span>
                    <span className="adm-num shrink-0 text-muted-foreground">
                      {service.jobs} · <span className="text-foreground">{fmt(service.revenue)}</span>
                    </span>
                  </div>
                  <div className="ml-6 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${(service.jobs / maxServiceJobs) * 100}%`, background: index === 0 ? VIOLET : 'hsl(var(--adm-violet) / 0.35)' }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
